import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  Drawer,
  IconButton,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  LinearProgress,
  Tooltip,
} from "@mui/material";
import {
  Close as CloseIcon,
  ArrowBack as BackIcon,
  Edit as EditIcon,
  SmartToy as GptIcon,
  ExpandMore as ExpandMoreIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  CheckCircle as CheckIcon,
  Schedule as PendingIcon,
  PlayArrow as RunningIcon,
  Email as EmailIcon,
} from "@mui/icons-material";
import AdminPage from "../AdminPage";
import { apiClient } from "../../utils/apiClient";
import { useSnackbar } from "../../components/SnackbarProvider";
import { useDispatch } from "react-redux";
import { openEmailModal } from "../../store/adminSlice";
import { TinyValueComponent } from "../../internal/components/TinyValueComponent";

interface PipelineResult {
  emailId: string;
  emailSubject?: string;
  categorizationStep?: {
    categories: string[];
    exchangeType: string;
    oneLineSummary: string;
    context: string;
  };
  propertyExtractionStep?: {
    properties: Array<{ name: string; value: string }>;
  };
  apiFetchStep?: {
    data: Record<string, unknown>;
  };
  presentationStep?: {
    presentation: string;
  };
  infos: string[];
  warns: string[];
  errors: string[];
  gptRequests: Array<{ name: string; id: string }>;
}

interface SavedResult {
  id: string;
  configId: string;
  configSnapshot: any;
  name: string | null;
  testSetId: string | null;
  testSetName: string | null;
  emailIds: string[];
  results: PipelineResult[];
  stepsRun: string[];
  runAt: string;
  workflowId: string | null;
  workflowStatus: string;
  processedCount: number;
  totalCount: number;
  errorMessage: string | null;
  createdAt: string;
  updatedAt: string;
}

export function EmailAutomationPipelineResultsPage() {
  const { resultId } = useParams<{ resultId: string }>();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const dispatch = useDispatch();

  const [savedResult, setSavedResult] = useState<SavedResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedResult, setSelectedResult] = useState<PipelineResult | null>(
    null
  );
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editNameDialogOpen, setEditNameDialogOpen] = useState(false);
  const [editName, setEditName] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (resultId) {
      loadResult();
    }
  }, [resultId]);

  // Poll for progress if workflow is still running
  useEffect(() => {
    if (
      !savedResult ||
      savedResult.workflowStatus === "completed" ||
      savedResult.workflowStatus === "failed"
    ) {
      return;
    }

    const pollInterval = setInterval(async () => {
      try {
        const res = await apiClient.post<{ result: SavedResult }>(
          `/admin/email-automation/results/get`,
          { id: resultId }
        );
        setSavedResult(res.result);

        if (
          res.result.workflowStatus === "completed" ||
          res.result.workflowStatus === "failed"
        ) {
          clearInterval(pollInterval);
          if (res.result.workflowStatus === "completed") {
            showSnackbar("Pipeline completed!", 3000, "success");
          } else {
            showSnackbar(
              `Pipeline failed: ${res.result.errorMessage || "Unknown error"}`,
              4000,
              "error"
            );
          }
        }
      } catch (error) {
        console.error("Error polling result:", error);
      }
    }, 2000);

    return () => clearInterval(pollInterval);
  }, [savedResult, resultId, showSnackbar]);

  const loadResult = async () => {
    try {
      setLoading(true);
      const res = await apiClient.post<{ result: SavedResult }>(
        `/admin/email-automation/results/get`,
        { id: resultId }
      );
      setSavedResult(res.result);
    } catch (error) {
      console.error("Error loading result:", error);
      showSnackbar("Failed to load result", 4000, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = (result: PipelineResult) => {
    setSelectedResult(result);
    setDrawerOpen(true);
  };

  const handleOpenEmail = (emailId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    dispatch(openEmailModal(emailId));
  };

  const handleOpenEditName = () => {
    setEditName(savedResult?.name || "");
    setEditNameDialogOpen(true);
  };

  const handleSaveName = async () => {
    if (!resultId || !editName.trim()) return;

    setSaving(true);
    try {
      await apiClient.put(`/admin/email-automation/results/update`, {
        id: resultId,
        name: editName.trim(),
      });
      showSnackbar("Name updated successfully", 3000, "success");
      setEditNameDialogOpen(false);
      loadResult();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.error || "Failed to update name";
      showSnackbar(errorMessage, 4000, "error");
    } finally {
      setSaving(false);
    }
  };

  const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "completed":
        return <CheckIcon color="success" />;
      case "failed":
        return <ErrorIcon color="error" />;
      case "running":
        return <RunningIcon color="primary" />;
      default:
        return <PendingIcon color="disabled" />;
    }
  };

  if (loading) {
    return (
      <AdminPage title="Pipeline Results">
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      </AdminPage>
    );
  }

  if (!savedResult) {
    return (
      <AdminPage title="Pipeline Results">
        <Box sx={{ p: 3 }}>
          <Alert severity="error">Result not found</Alert>
          <Button
            startIcon={<BackIcon />}
            onClick={() => navigate("/admin/email-automation")}
            sx={{ mt: 2 }}
          >
            Back to Configurations
          </Button>
        </Box>
      </AdminPage>
    );
  }

  const results = savedResult.results || [];
  const isRunning =
    savedResult.workflowStatus === "running" ||
    savedResult.workflowStatus === "pending";

  return (
    <AdminPage
      title={
        savedResult.name ||
        `Results: ${savedResult.configSnapshot?.name || "Unknown"}`
      }
    >
      <Box sx={{ p: 3 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Button
            startIcon={<BackIcon />}
            onClick={() => navigate("/admin/email-automation")}
          >
            Back to Configurations
          </Button>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={handleOpenEditName}
          >
            Edit Name
          </Button>
        </Box>

        {/* Status Bar */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box display="flex" alignItems="center" gap={2}>
            {getStatusIcon(savedResult.workflowStatus)}
            <Box flex={1}>
              <Typography variant="body2">
                Status: <strong>{savedResult.workflowStatus}</strong>
              </Typography>
              {isRunning && (
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 2, mt: 1 }}
                >
                  <Box sx={{ flex: 1 }}>
                    <LinearProgress
                      variant="determinate"
                      value={
                        savedResult.totalCount > 0
                          ? (savedResult.processedCount /
                              savedResult.totalCount) *
                            100
                          : 0
                      }
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {savedResult.processedCount} / {savedResult.totalCount}
                  </Typography>
                </Box>
              )}
            </Box>
            <Typography variant="body2" color="text.secondary">
              {results.length} of {savedResult.emailIds.length} email(s)
            </Typography>
          </Box>
          {savedResult.errorMessage && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {savedResult.errorMessage}
            </Alert>
          )}
        </Paper>

        {results.length === 0 && !isRunning ? (
          <Alert severity="info">No results available yet.</Alert>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Click a row to view details.
            </Typography>

            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Email ID</TableCell>
                    <TableCell>Categories</TableCell>
                    <TableCell>Properties</TableCell>
                    <TableCell>Fetched Data</TableCell>
                    <TableCell>Presentation</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {results.map((result) => (
                    <TableRow
                      key={result.emailId}
                      hover
                      sx={{ cursor: "pointer" }}
                      onClick={() => handleRowClick(result)}
                    >
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <IconButton
                            size="small"
                            onClick={(e) => handleOpenEmail(result.emailId, e)}
                            title="Open email"
                            sx={{ p: 0.25 }}
                          >
                            <EmailIcon fontSize="small" color="primary" />
                          </IconButton>
                          <Box>
                            <TinyValueComponent
                              value={result.emailId}
                              clipboard
                              cropText
                            />
                            {result.emailSubject && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                  display: "block",
                                  maxWidth: 200,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {result.emailSubject}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}
                        >
                          {result.categorizationStep?.categories
                            .slice(0, 3)
                            .map((cat) => (
                              <Chip key={cat} label={cat} size="small" />
                            ))}
                          {(result.categorizationStep?.categories.length || 0) >
                            3 && (
                            <Chip
                              label={`+${result.categorizationStep!.categories.length - 3}`}
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {result.propertyExtractionStep?.properties.length ? (
                          <Tooltip
                            title={
                              <Box>
                                {result.propertyExtractionStep.properties.map(
                                  (p, idx) => (
                                    <Typography
                                      key={idx}
                                      variant="body2"
                                      sx={{ whiteSpace: "nowrap" }}
                                    >
                                      {p.name}: {p.value}
                                    </Typography>
                                  )
                                )}
                              </Box>
                            }
                            arrow
                          >
                            <Box>
                              {result.propertyExtractionStep.properties
                                .slice(0, 2)
                                .map((p, idx) => (
                                  <Typography
                                    key={idx}
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{
                                      overflow: "hidden",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {p.name}: {p.value}
                                  </Typography>
                                ))}
                              {result.propertyExtractionStep.properties.length > 2 && (
                                <Typography
                                  variant="caption"
                                  color="primary"
                                  sx={{ fontWeight: 500 }}
                                >
                                  (+{result.propertyExtractionStep.properties.length - 2})
                                </Typography>
                              )}
                            </Box>
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            -
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            maxWidth: 150,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {result.apiFetchStep?.data
                            ? Object.keys(result.apiFetchStep.data).join(", ")
                            : "-"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            maxWidth: 200,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {truncateText(
                            result.presentationStep?.presentation || "-",
                            50
                          )}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 0.5 }}>
                          {result.errors.length > 0 && (
                            <Chip
                              icon={<ErrorIcon />}
                              label={result.errors.length}
                              size="small"
                              color="error"
                            />
                          )}
                          {result.warns.length > 0 && (
                            <Chip
                              icon={<WarningIcon />}
                              label={result.warns.length}
                              size="small"
                              color="warning"
                            />
                          )}
                          {result.gptRequests.length > 0 && (
                            <Chip
                              icon={<GptIcon />}
                              label={result.gptRequests.length}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {/* Detail Drawer */}
        <Drawer
          anchor="right"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          sx={{ "& .MuiDrawer-paper": { width: { xs: "100%", md: 600 } } }}
        >
          {selectedResult && (
            <Box sx={{ p: 2 }}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Typography variant="h6">Result Details</Typography>
                <IconButton onClick={() => setDrawerOpen(false)}>
                  <CloseIcon />
                </IconButton>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <IconButton
                  size="small"
                  onClick={(e) => handleOpenEmail(selectedResult.emailId, e)}
                  title="Open email"
                >
                  <EmailIcon color="primary" />
                </IconButton>
                <Typography variant="subtitle2" color="text.secondary">
                  Email ID:
                </Typography>
                <TinyValueComponent
                  value={selectedResult.emailId}
                  clipboard
                />
              </Box>

              {/* Categorization Step */}
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>Categorization</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {selectedResult.categorizationStep ? (
                    <>
                      <Typography variant="body2" fontWeight="medium">
                        Categories:
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          flexWrap: "wrap",
                          mb: 2,
                        }}
                      >
                        {selectedResult.categorizationStep.categories.map(
                          (cat) => (
                            <Chip key={cat} label={cat} size="small" />
                          )
                        )}
                      </Box>
                      <Typography variant="body2" fontWeight="medium">
                        Exchange Type:
                      </Typography>
                      <Typography variant="body2" mb={2}>
                        {selectedResult.categorizationStep.exchangeType}
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        Summary:
                      </Typography>
                      <Typography variant="body2" mb={2}>
                        {selectedResult.categorizationStep.oneLineSummary}
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        Context:
                      </Typography>
                      <Typography variant="body2">
                        {selectedResult.categorizationStep.context}
                      </Typography>
                    </>
                  ) : (
                    <Typography color="text.secondary">Not executed</Typography>
                  )}
                </AccordionDetails>
              </Accordion>

              {/* Property Extraction Step */}
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>Property Extraction</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {selectedResult.propertyExtractionStep ? (
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Property</TableCell>
                          <TableCell>Value</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedResult.propertyExtractionStep.properties.map(
                          (prop, idx) => (
                            <TableRow key={idx}>
                              <TableCell>{prop.name}</TableCell>
                              <TableCell>{prop.value}</TableCell>
                            </TableRow>
                          )
                        )}
                      </TableBody>
                    </Table>
                  ) : (
                    <Typography color="text.secondary">Not executed</Typography>
                  )}
                </AccordionDetails>
              </Accordion>

              {/* API Fetch Step */}
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>API Fetch</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {selectedResult.apiFetchStep ? (
                    <Paper
                      sx={{
                        p: 2,
                        backgroundColor: "grey.50",
                        fontFamily: "monospace",
                        fontSize: "0.75rem",
                        whiteSpace: "pre-wrap",
                        maxHeight: 300,
                        overflow: "auto",
                      }}
                    >
                      {JSON.stringify(
                        selectedResult.apiFetchStep.data,
                        null,
                        2
                      )}
                    </Paper>
                  ) : (
                    <Typography color="text.secondary">Not executed</Typography>
                  )}
                </AccordionDetails>
              </Accordion>

              {/* Presentation Step */}
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>Presentation</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {selectedResult.presentationStep ? (
                    <Paper
                      sx={{
                        p: 2,
                        backgroundColor: "grey.50",
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      {selectedResult.presentationStep.presentation}
                    </Paper>
                  ) : (
                    <Typography color="text.secondary">Not executed</Typography>
                  )}
                </AccordionDetails>
              </Accordion>

              {/* Logs */}
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>
                    Logs ({selectedResult.infos.length} info,{" "}
                    {selectedResult.warns.length} warn,{" "}
                    {selectedResult.errors.length} error)
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <List dense>
                    {selectedResult.errors.map((msg, idx) => (
                      <ListItem key={`error-${idx}`}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <ErrorIcon color="error" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={msg} />
                      </ListItem>
                    ))}
                    {selectedResult.warns.map((msg, idx) => (
                      <ListItem key={`warn-${idx}`}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <WarningIcon color="warning" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={msg} />
                      </ListItem>
                    ))}
                    {selectedResult.infos.map((msg, idx) => (
                      <ListItem key={`info-${idx}`}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <InfoIcon color="info" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={msg} />
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>

              {/* GPT Requests */}
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>
                    GPT Requests ({selectedResult.gptRequests.length})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <List dense>
                    {selectedResult.gptRequests.map((req, idx) => (
                      <ListItem
                        key={idx}
                        component="a"
                        href={`/www/admin/request/${req.id}`}
                        target="_blank"
                        sx={{
                          textDecoration: "none",
                          color: "inherit",
                          "&:hover": { backgroundColor: "action.hover" },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <GptIcon color="primary" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary={req.name}
                          secondary={`ID: ${req.id}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            </Box>
          )}
        </Drawer>

        {/* Edit Name Dialog */}
        <Dialog
          open={editNameDialogOpen}
          onClose={() => setEditNameDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Edit Result Name</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label="Name"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              sx={{ mt: 1 }}
              autoFocus
              placeholder="e.g., Booking Emails Test Run 2024-01-15"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditNameDialogOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleSaveName}
              disabled={saving || !editName.trim()}
            >
              {saving ? <CircularProgress size={20} /> : "Save"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AdminPage>
  );
}
